import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import { getAuthHeaders, setPartnerMeta, getPartnerMeta } from "../lib/adminAuth";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function AdminPartnerChangePassword() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("Passwort muss mindestens 8 Zeichen lang sein."); return; }
    if (next !== confirm) { setErr("Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await axios.post(
        `${API}/partner/change-password`,
        { current_password: current, new_password: next },
        { headers: getAuthHeaders() }
      );
      const meta = getPartnerMeta();
      if (meta) setPartnerMeta({ ...meta, must_change_password: false });
      setDone(true);
      setTimeout(() => {
        const next = sessionStorage.getItem("alphalane_login_next");
        sessionStorage.removeItem("alphalane_login_next");
        navigate(next || "/partner/coaching");
      }, 1200);
    } catch (e) {
      setErr(e.response?.data?.detail || "Konnte Passwort nicht ändern.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] flex items-center justify-center p-5" data-testid="partner-change-pw">
      <div className="al-card w-full max-w-md p-8 sm:p-10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Alphalane" className="h-10 w-10 object-contain" />
          <div className="font-display font-semibold text-base">
            <span className="al-grad-text">ALPHALANE</span>
            <span className="ml-2 text-xs uppercase tracking-widest text-slate-500">Partner · Passwort</span>
          </div>
        </div>
        <h1 className="mt-7 font-display font-bold text-2xl">Wähle ein eigenes Passwort.</h1>
        <p className="mt-2 text-sm text-slate-600">Aus Sicherheitsgründen — danach geht's direkt weiter.</p>
        {done ? (
          <div className="mt-6 flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" /> Passwort aktualisiert. Weiterleiten…
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <PwField label="Aktuelles Passwort" value={current} onChange={setCurrent} testid="current-pw" />
            <PwField label="Neues Passwort (min. 8 Zeichen)" value={next} onChange={setNext} testid="new-pw" />
            <PwField label="Neues Passwort wiederholen" value={confirm} onChange={setConfirm} testid="confirm-pw" />
            {err && <div className="flex items-center gap-2 text-sm text-rose-600"><AlertCircle className="h-4 w-4" />{err}</div>}
            <button type="submit" disabled={loading} data-testid="submit-change-pw" className="al-cta w-full justify-center disabled:opacity-60">
              {loading ? "Speichere…" : "Passwort ändern"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const PwField = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
      <Lock className="h-3.5 w-3.5" /> {label}
    </span>
    <input
      type="password" required
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[var(--al-purple)] outline-none text-sm"
      placeholder="••••••••••••"
    />
  </label>
);
